import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { format } from 'date-fns';
import cx from 'classnames';
import Link from 'next/link';

import Icon from 'components/Icon';
import DatePosted from 'components/DatePosted';
import Deadline from 'components/Deadline';
import IconButton from 'components/IconButton';
import Avatar from 'components/Avatar';
import { useResponsive } from 'hooks/useResponsive';
import { BasicOpportunity } from 'lib/models/opportunity';
import { TouchpointType } from 'lib/models/touchpoint';
import { useAuth } from 'lib/providers/AuthProvider';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import styles from './OpportunityCard.module.scss';

type OpportunityCardProps = {
  opportunity: BasicOpportunity;
  onSelect: (opportunity: BasicOpportunity) => void;
  isSelected?: boolean;
  isExpandable?: boolean;
  className?: string;
  showExpandIcon?: boolean;
};

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, onSelect, isSelected, className, isExpandable, showExpandIcon }) => {
  const { isNonOnboardedUser } = useAuth();
  const screens = useResponsive();
  const [tCommon] = useTranslation('common');
  const {
    title,
    slug,
    touchpointable_type,
    basic_creatable_for,
    created_at,
    posted_at,
    application_deadline,
    start_date,
    featured,
    is_in_current_matches,
    icon_url: banner_url,
  } = opportunity;
  const { icon_url, name, is_huzzle_verified, is_verified } = basic_creatable_for;
  const publicPageUrl = getOpportunityPublicPagePathname(touchpointable_type, slug);
  const event_icon_url = banner_url || `${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/images/cover-image.png`;

  const eventStartDate = useMemo(() => {
    if (!start_date) return;

    return format(new Date(created_at), 'MMM dd, hh:mm a');
  }, [start_date]);

  const handleAnchorClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.stopPropagation();
  };

  const isAnEvent = touchpointable_type === TouchpointType.Event;

  const content = (
    <section className={cx(styles.container, className, { [styles.selected]: isSelected })} onClick={() => !screens.sm && onSelect(opportunity)}>
      {!isAnEvent ? (
        <div className={styles.image}>
          <Avatar name={name} icon={icon_url} isHuzzleVerified={is_huzzle_verified} isVerified={is_verified} size="medium" />
        </div>
      ) : null}
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.left}>
            <div className={styles.tags}>
              {featured && <div className={styles.tag}>{tCommon('featured')}</div>}
              {is_in_current_matches && !isNonOnboardedUser && <div className={styles.tag}>{tCommon('your-match')}</div>}
            </div>
            {isAnEvent && eventStartDate && <p className={styles['event-date']}>{eventStartDate}</p>}
            <h3 className={styles.title}>{title}</h3>
          </div>
          {!isAnEvent &&
            (isExpandable ? (
              showExpandIcon && <IconButton iconName="icon_arrows-maximize-2" className={cx(styles.action, styles['no-hover'])} />
            ) : (
              <a href={publicPageUrl} target="_blank" onClick={handleAnchorClick} className={styles.action} rel="noreferrer">
                <Icon iconName="icon_launch" size="auto" />
              </a>
            ))}
        </div>
        <div className={styles['posted-by-container']}>
          <p className={styles['posted-by']}>{name}</p>
          {!isAnEvent && (
            <>
              <div className={styles.dot}>•</div>
              <DatePosted className={styles.date} created_at={posted_at || created_at} />
            </>
          )}
        </div>
        {application_deadline && !isAnEvent && <Deadline application_deadline={application_deadline} className={styles.deadline} />}
      </div>
      {isAnEvent && (
        <div className={styles['banner-container']}>
          {event_icon_url ? <img src={event_icon_url} className={styles.banner} /> : null}
          <a href={publicPageUrl} target="_blank" onClick={handleAnchorClick} className={styles.action} rel="noreferrer">
            <Icon iconName="icon_launch" size="auto" />
          </a>
        </div>
      )}
    </section>
  );

  return screens.sm ? (
    <Link href={publicPageUrl} passHref>
      <a>{content}</a>
    </Link>
  ) : (
    content
  );
};

OpportunityCard.displayName = 'OpportunityCard';

export default OpportunityCard;
